import { ChangeEvent, useState } from 'react';
import { SortDirectionEnum } from '../../../legacy/shared-types';
import { TablePagination } from '@mui/material';
import { useProductsList } from 'queries';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from 'components/common';
import { ProductsToolbar } from './ProductsToolbar';
import { ProductRow } from './ProductRow';
import { HeaderCell } from './HeaderCell';
import { useProductSortAndFilters } from '../../../hooks/useProductSortAndFilters/useProductSortAndFilters';
import { FiltersKeys, IProductFilters, tableColumnsNames, ALL_FILTER_OPTION, TableColumns } from './types';
import { FormattedMessage } from 'react-intl';
import { useDebounce } from 'hooks';

const FIRST_EMPTY_COLUMN = 1;
const ROWS_PER_PAGE = 100;

export const ProductsTable = () => {
  const { data: productList = [], isLoading } = useProductsList();
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.ASC);
  const [orderBy, setOrderBy] = useState<TableColumns>('isActive');
  const [searchedValue, setSearchedValue] = useState('');
  const debouncedSearchValue = useDebounce(searchedValue, 300);

  const [page, setPage] = useState(0);
  const [filtersState, setFiltersState] = useState<IProductFilters>({
    productCategory: ALL_FILTER_OPTION,
    ftv: ALL_FILTER_OPTION,
    productType: ALL_FILTER_OPTION,
    isActiveProductsOnly: false,
    productRange: ALL_FILTER_OPTION,
  });

  const filterHandler = useProductSortAndFilters(order, orderBy, debouncedSearchValue, filtersState);
  const sortedAndFilteredRows = filterHandler(productList);

  const setFiltersHandler = (key: FiltersKeys, value: string | number | boolean) => {
    setFiltersState(prevState => ({ ...prevState, [key]: value }));

    if (page > 0) setPage(0);
  };

  const columnSortHandler = (columnId: TableColumns) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === columnId;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(columnId);
  };

  const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchedValue(event.target.value);
    if (page > 0) setPage(0);
  };

  return (
    <>
      <ProductsToolbar onSearch={searchHandler} productList={productList} onFiltersChange={setFiltersHandler} />

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
          <Table aria-label='collapsible table'>
            <TableHead>
              <TableRow>
                <TableCell />
                {tableColumnsNames.map(columnTextId => (
                  <HeaderCell
                    columnTextId={columnTextId}
                    order={order}
                    orderBy={orderBy}
                    key={columnTextId}
                    sortHandler={columnSortHandler}
                  />
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length + FIRST_EMPTY_COLUMN}>
              {sortedAndFilteredRows.length ? (
                sortedAndFilteredRows
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((row, index) => <ProductRow key={row.code} index={index} {...row} />)
              ) : (
                <TableRow>
                  <TableCell sx={{ textAlign: 'center' }} colSpan={9}>
                    <FormattedMessage id={'common.itemsNotFound'} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!!sortedAndFilteredRows.length && (
          <TablePagination
            component='div'
            count={sortedAndFilteredRows.length}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[]}
            page={page}
            onPageChange={(_, page) => setPage(page)}
          />
        )}
      </Paper>
    </>
  );
};
